import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { RefreshCw, ArrowUpRight, ArrowDownRight, Settings, Eye, Layers, List, Copy } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export function MarketEvolutionView({ currentProject, onFilterClick }) {
  const [deltaData, setDeltaData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState(20);
  const [groupByMatch, setGroupByMatch] = useState(false); // Grouper par match ou détail
  const [courtFilter, setCourtFilter] = useState('all'); // Filtre par court

  // Fetch delta data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API}/api/delta?project=${encodeURIComponent(currentProject)}`);
        const data = await res.json();
        setDeltaData(Array.isArray(data) ? data : (data.events || []));
      } catch (error) {
        console.error('Error fetching delta:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentProject]);

  // Extraire la liste des courts disponibles
  const availableCourts = useMemo(() => {
    const courts = new Set();
    deltaData.forEach(event => {
      if (event.lieu) {
        courts.add(event.lieu);
      }
    });
    return Array.from(courts).sort();
  }, [deltaData]);

  // Analyser les données pour les alertes (avec filtre par court)
  const analysis = useMemo(() => {
    const alerts = { buy: [], sell: [] };
    const matchGroups = { buy: {}, sell: {} };

    // Filtrer par court si nécessaire
    const filteredData = courtFilter === 'all' 
      ? deltaData 
      : deltaData.filter(event => event.lieu === courtFilter);

    filteredData.forEach(event => {
      const matchNum = event.evenement?.match(/\bM\d+\b/)?.[0] || `${event.evenement || 'Sans événement'}${event.date_evenement ? ` (${event.date_evenement})` : ''}`;
      const stocks = event.stocks || {};

      Object.entries(stocks).forEach(([cat, stock]) => {
        if (stock < -threshold) {
          alerts.buy.push({ 
            match: matchNum, 
            category: cat, 
            stock, 
            event: event.evenement,
            date: event.date_evenement,
            court: event.lieu,
            fullEvent: event 
          });
          // Grouper par match
          if (!matchGroups.buy[matchNum]) {
            matchGroups.buy[matchNum] = { match: matchNum, totalStock: 0, categories: [], event: event.evenement, date: event.date_evenement, court: event.lieu };
          }
          matchGroups.buy[matchNum].totalStock += stock;
          matchGroups.buy[matchNum].categories.push({ cat, stock });
        } else if (stock > threshold) {
          alerts.sell.push({ 
            match: matchNum, 
            category: cat, 
            stock, 
            event: event.evenement,
            date: event.date_evenement,
            court: event.lieu,
            fullEvent: event 
          });
          // Grouper par match
          if (!matchGroups.sell[matchNum]) {
            matchGroups.sell[matchNum] = { match: matchNum, totalStock: 0, categories: [], event: event.evenement, date: event.date_evenement, court: event.lieu };
          }
          matchGroups.sell[matchNum].totalStock += stock;
          matchGroups.sell[matchNum].categories.push({ cat, stock });
        }
      });
    });

    // Trier
    alerts.buy.sort((a, b) => a.stock - b.stock);
    alerts.sell.sort((a, b) => b.stock - a.stock);

    // Convertir les groupes en array et trier
    const groupedBuy = Object.values(matchGroups.buy).sort((a, b) => a.totalStock - b.totalStock);
    const groupedSell = Object.values(matchGroups.sell).sort((a, b) => b.totalStock - a.totalStock);

    return { alerts, groupedBuy, groupedSell };
  }, [deltaData, threshold, courtFilter]);

  // Gérer le clic sur une ligne
  const handleRowClick = (alert, type) => {
    if (onFilterClick) {
      onFilterClick({
        match: alert.match,
        category: alert.category || '',
        event: alert.event,
        date: alert.date,
        type: type
      });
    }
  };

  // Copier toutes les alertes
  const handleCopyAll = (type) => {
    let data;
    let title;
    
    if (type === 'buy') {
      data = groupByMatch ? analysis.groupedBuy : analysis.alerts.buy;
      title = 'ACHETER - Stock Critique';
    } else {
      data = groupByMatch ? analysis.groupedSell : analysis.alerts.sell;
      title = 'VENDRE - Surplus Stock';
    }

    let text = `${title}\n${'='.repeat(40)}\n`;
    
    if (groupByMatch) {
      data.forEach(item => {
        text += `${item.match}\t${item.totalStock}\t${item.categories.map(c => `${c.cat}:${c.stock}`).join(', ')}\n`;
      });
    } else {
      text += 'MATCH\tCATEGORIE\tSTOCK\n';
      data.forEach(item => {
        text += `${item.match}\t${item.category}\t${item.stock}\n`;
      });
    }

    navigator.clipboard.writeText(text).then(() => {
      toast.success(`✅ ${data.length} ligne(s) copiée(s) !`);
    }).catch(() => {
      toast.error('Erreur lors de la copie');
    });
  };

  // Copier tout (les deux colonnes)
  const handleCopyBoth = () => {
    let text = `RECAP DELTA - ${currentProject}\n${'='.repeat(50)}\n\n`;
    
    // ACHETER
    text += `🔴 ACHETER - Stock Critique (${groupByMatch ? analysis.groupedBuy.length : analysis.alerts.buy.length})\n`;
    text += '-'.repeat(40) + '\n';
    if (groupByMatch) {
      analysis.groupedBuy.forEach(item => {
        text += `${item.match}\t${item.totalStock}\n`;
      });
    } else {
      analysis.alerts.buy.forEach(item => {
        text += `${item.match}\t${item.category}\t${item.stock}\n`;
      });
    }
    
    text += '\n';
    
    // VENDRE
    text += `🟢 VENDRE - Surplus Stock (${groupByMatch ? analysis.groupedSell.length : analysis.alerts.sell.length})\n`;
    text += '-'.repeat(40) + '\n';
    if (groupByMatch) {
      analysis.groupedSell.forEach(item => {
        text += `${item.match}\t+${item.totalStock}\n`;
      });
    } else {
      analysis.alerts.sell.forEach(item => {
        text += `${item.match}\t${item.category}\t+${item.stock}\n`;
      });
    }

    navigator.clipboard.writeText(text).then(() => {
      const total = (groupByMatch ? analysis.groupedBuy.length + analysis.groupedSell.length : analysis.alerts.buy.length + analysis.alerts.sell.length);
      toast.success(`✅ ${total} ligne(s) copiée(s) !`);
    }).catch(() => {
      toast.error('Erreur lors de la copie');
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-[#C4A052]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="recap-delta-view">
      {/* Contrôles */}
      <div className="flex items-center justify-between bg-zinc-800/50 p-4 rounded-lg border border-zinc-700">
        <div className="flex items-center gap-6">
          {/* Seuil */}
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-[#C4A052]" />
            <span className="text-white font-medium">Seuil :</span>
            <div className="flex gap-1">
              {[10, 20, 30, 40, 50].map(val => (
                <Button
                  key={val}
                  size="sm"
                  variant={threshold === val ? "default" : "outline"}
                  onClick={() => setThreshold(val)}
                  className={threshold === val 
                    ? "bg-[#C4A052] text-black hover:bg-[#D4B86A] h-8 px-3" 
                    : "border-zinc-600 text-zinc-400 hover:text-white h-8 px-3"
                  }
                >
                  {val}
                </Button>
              ))}
            </div>
          </div>

          {/* Grouper par match */}
          <div className="flex items-center gap-2 border-l border-zinc-600 pl-4">
            <Button
              size="sm"
              variant={!groupByMatch ? "default" : "outline"}
              onClick={() => setGroupByMatch(false)}
              className={!groupByMatch 
                ? "bg-blue-600 text-white hover:bg-blue-700 h-8" 
                : "border-zinc-600 text-zinc-400 hover:text-white h-8"
              }
            >
              <List className="w-4 h-4 mr-1" />
              Détail
            </Button>
            <Button
              size="sm"
              variant={groupByMatch ? "default" : "outline"}
              onClick={() => setGroupByMatch(true)}
              className={groupByMatch 
                ? "bg-blue-600 text-white hover:bg-blue-700 h-8" 
                : "border-zinc-600 text-zinc-400 hover:text-white h-8"
              }
            >
              <Layers className="w-4 h-4 mr-1" />
              Par Match
            </Button>
          </div>

          {/* Filtre par Court/Lieu */}
          {availableCourts.length > 0 && (
            <div className="flex items-center gap-2 border-l border-zinc-600 pl-4">
              <span className="text-zinc-400 text-sm">Court:</span>
              <select
                value={courtFilter}
                onChange={(e) => setCourtFilter(e.target.value)}
                className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-[#C4A052] max-w-[200px]"
              >
                <option value="all">🏟️ Tous les courts</option>
                {availableCourts.map(court => (
                  <option key={court} value={court}>
                    {court.length > 25 ? court.substring(0, 25) + '...' : court}
                  </option>
                ))}
              </select>
              {courtFilter !== 'all' && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setCourtFilter('all')}
                  className="h-6 px-2 text-red-400 hover:text-red-300"
                >
                  ✕
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Copier tout */}
          <Button
            onClick={handleCopyBoth}
            variant="outline"
            className="border-[#C4A052] text-[#C4A052] hover:bg-[#C4A052]/20"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copier Tout
          </Button>
          
          {/* Tout afficher */}
          <Button
            onClick={() => setThreshold(0)}
            variant={threshold === 0 ? "default" : "outline"}
            className={threshold === 0 
              ? "bg-purple-600 text-white hover:bg-purple-700" 
              : "border-purple-500 text-purple-400 hover:bg-purple-500/20"
            }
          >
            <Eye className="w-4 h-4 mr-2" />
            TOUT
          </Button>
        </div>
      </div>

      {/* Info seuil */}
      <div className="text-center text-sm text-zinc-500">
        Affichage : ACHETER si stock &lt; <span className="text-red-400 font-bold">-{threshold}</span> | VENDRE si stock &gt; <span className="text-emerald-400 font-bold">+{threshold}</span>
        {threshold === 0 && <span className="ml-2 text-purple-400">(Tout affiché)</span>}
        {groupByMatch && <span className="ml-2 text-blue-400">(Groupé par match)</span>}
      </div>

      {/* Colonnes ACHETER / VENDRE */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ACHETER */}
        <Card className="bg-zinc-900/80 border-red-500/30 border-l-4 border-l-red-500">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl flex items-center gap-2 text-red-400">
                <ArrowDownRight className="w-6 h-6" />
                🔴 ACHETER - Stock Critique
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleCopyAll('buy')}
                  className="text-red-400 hover:bg-red-500/20 h-8"
                >
                  <Copy className="w-4 h-4" />
                </Button>
                <Badge variant="destructive" className="text-lg px-3">
                  {groupByMatch ? analysis.groupedBuy.length : analysis.alerts.buy.length}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="max-h-[500px] overflow-y-auto">
            {(groupByMatch ? analysis.groupedBuy : analysis.alerts.buy).length === 0 ? (
              <p className="text-zinc-500 text-center py-8">Aucune alerte</p>
            ) : (
              <div className="space-y-2">
                {groupByMatch ? (
                  // Vue groupée par match
                  analysis.groupedBuy.map((group, i) => (
                    <div 
                      key={i} 
                      onClick={() => handleRowClick({ match: group.match, event: group.event, date: group.date }, 'buy')}
                      className="p-3 bg-red-500/10 rounded-lg border border-red-500/20 hover:bg-red-500/30 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-white text-lg">{group.match}</span>
                        <Badge variant="destructive" className="font-mono text-base px-3 py-1">
                          {group.totalStock}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {group.categories.map((c, j) => (
                          <span key={j} className="text-xs px-2 py-0.5 bg-red-500/20 rounded text-red-300">
                            {c.cat}: {c.stock}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  // Vue détaillée
                  analysis.alerts.buy.map((alert, i) => (
                    <div 
                      key={i} 
                      onClick={() => handleRowClick(alert, 'buy')}
                      className="flex items-center justify-between p-3 bg-red-500/10 rounded-lg border border-red-500/20 hover:bg-red-500/30 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-white text-lg">{alert.match}</span>
                        <span className="text-zinc-400">{alert.category}</span>
                        {alert.court && (
                          <span className="text-xs px-2 py-0.5 bg-zinc-700 rounded text-zinc-400">
                            🏟️ {alert.court.length > 20 ? alert.court.substring(0, 20) + '...' : alert.court}
                          </span>
                        )}
                      </div>
                      <Badge variant="destructive" className="font-mono text-base px-3 py-1">
                        {alert.stock}
                      </Badge>
                    </div>
                  ))
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* VENDRE */}
        <Card className="bg-zinc-900/80 border-emerald-500/30 border-l-4 border-l-emerald-500">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl flex items-center gap-2 text-emerald-400">
                <ArrowUpRight className="w-6 h-6" />
                🟢 VENDRE - Surplus Stock
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleCopyAll('sell')}
                  className="text-emerald-400 hover:bg-emerald-500/20 h-8"
                >
                  <Copy className="w-4 h-4" />
                </Button>
                <Badge className="bg-emerald-600 text-lg px-3">
                  {groupByMatch ? analysis.groupedSell.length : analysis.alerts.sell.length}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="max-h-[500px] overflow-y-auto">
            {(groupByMatch ? analysis.groupedSell : analysis.alerts.sell).length === 0 ? (
              <p className="text-zinc-500 text-center py-8">Aucune alerte</p>
            ) : (
              <div className="space-y-2">
                {groupByMatch ? (
                  // Vue groupée par match
                  analysis.groupedSell.map((group, i) => (
                    <div 
                      key={i} 
                      onClick={() => handleRowClick({ match: group.match, event: group.event, date: group.date }, 'sell')}
                      className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20 hover:bg-emerald-500/30 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-white text-lg">{group.match}</span>
                        <Badge className="bg-emerald-600 font-mono text-base px-3 py-1">
                          +{group.totalStock}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {group.categories.map((c, j) => (
                          <span key={j} className="text-xs px-2 py-0.5 bg-emerald-500/20 rounded text-emerald-300">
                            {c.cat}: +{c.stock}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  // Vue détaillée
                  analysis.alerts.sell.map((alert, i) => (
                    <div 
                      key={i} 
                      onClick={() => handleRowClick(alert, 'sell')}
                      className="flex items-center justify-between p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20 hover:bg-emerald-500/30 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-white text-lg">{alert.match}</span>
                        <span className="text-zinc-400">{alert.category}</span>
                        {alert.court && (
                          <span className="text-xs px-2 py-0.5 bg-zinc-700 rounded text-zinc-400">
                            🏟️ {alert.court.length > 20 ? alert.court.substring(0, 20) + '...' : alert.court}
                          </span>
                        )}
                      </div>
                      <Badge className="bg-emerald-600 font-mono text-base px-3 py-1">
                        +{alert.stock}
                      </Badge>
                    </div>
                  ))
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default MarketEvolutionView;
